// client/src/services/tokenService.js
import axios from 'axios';

// Durate della sessione: devono restare allineate a server/config/config.js (session)
export const IDLE_TIMEOUT = 45 * 60 * 1000;        // logout dopo 45 min senza attività
export const IDLE_WARNING = 2 * 60 * 1000;         // avviso 2 min prima del logout per inattività
export const SESSION_MAX = 10 * 60 * 60 * 1000;    // durata massima della sessione dal login
export const SESSION_END_WARNING = 5 * 60 * 1000;  // avviso 5 min prima della fine sessione
const REFRESH_AFTER = 10 * 60 * 1000;              // rinnova il token quando ha 10 min, se c'è stata attività
const CHECK_INTERVAL = 30 * 1000;                  // frequenza del controllo per il refresh

// Attività condivisa tra le schede: chi lavora in una scheda tiene viva la sessione anche nelle altre
const ACTIVITY_KEY = 'lastActivity';
const ACTIVITY_WRITE_THROTTLE = 5 * 1000;

class TokenService {
  constructor() {
    this.refreshTimerId = null;
    this.refreshingPromise = null;
    this.lastActivityTime = Date.now();
    this.lastActivityWrite = 0;
    this.activityLocked = false;
    this.onRefreshSuccess = null;
    this.onRefreshError = null;
  }

  // Update last activity time (only real user interaction, not API calls)
  updateActivity() {
    if (this.activityLocked) return;

    const now = Date.now();
    this.lastActivityTime = now;

    if (now - this.lastActivityWrite >= ACTIVITY_WRITE_THROTTLE) {
      this.lastActivityWrite = now;
      try {
        localStorage.setItem(ACTIVITY_KEY, String(now));
      } catch (e) {
        // storage non disponibile: resta l'attività della singola scheda
      }
    }
  }

  // While the idle warning is shown, only an explicit "continue" counts as activity
  setActivityLocked(locked) {
    this.activityLocked = locked;
  }

  // Get last activity time (latest among all open tabs)
  getLastActivityTime() {
    let shared = 0;
    try {
      shared = Number(localStorage.getItem(ACTIVITY_KEY)) || 0;
    } catch (e) {
      shared = 0;
    }
    return Math.max(this.lastActivityTime, shared);
  }

  getIdleTime() {
    return Date.now() - this.getLastActivityTime();
  }

  // Times of the current token in ms: issuedAt, expiresAt, sessionStart
  getTokenInfo() {
    const token = localStorage.getItem('token');
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      return {
        issuedAt: payload.iat * 1000,
        expiresAt: payload.exp * 1000,
        sessionStart: (payload.sessionStart || payload.iat) * 1000
      };
    } catch (e) {
      return null;
    }
  }

  // Start automatic token refresh
  startTokenRefresh(onRefreshSuccess, onRefreshError) {
    this.stopTokenRefresh();

    this.onRefreshSuccess = onRefreshSuccess;
    this.onRefreshError = onRefreshError;

    if (!localStorage.getItem('token')) {
      return;
    }

    this.refreshTimerId = setInterval(() => {
      const info = this.getTokenInfo();
      if (!info) {
        this.stopTokenRefresh();
        return;
      }

      const now = Date.now();
      const tokenAge = now - info.issuedAt;
      const activeSinceIssue = this.getLastActivityTime() > info.issuedAt;

      // Rinnova solo se l'utente ha interagito dall'ultimo rinnovo:
      // un utente inattivo lascia scadere il token
      if (tokenAge >= REFRESH_AFTER && activeSinceIssue && this.getIdleTime() < IDLE_TIMEOUT) {
        this.refreshToken().catch(() => {});
      }
    }, CHECK_INTERVAL);
  }

  // Stop automatic token refresh
  stopTokenRefresh() {
    if (this.refreshTimerId) {
      clearInterval(this.refreshTimerId);
      this.refreshTimerId = null;
    }
  }

  // Perform token refresh
  async refreshToken() {
    // If a refresh is already in progress, wait for it to complete
    if (this.refreshingPromise) {
      return this.refreshingPromise;
    }

    if (!localStorage.getItem('token')) {
      return Promise.reject(new Error('No token to refresh'));
    }

    this.refreshingPromise = (async () => {
      try {
        const response = await axios.get('/api/auth/refresh-token');
        const newToken = response.data.token;

        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        localStorage.setItem('token', newToken);

        if (this.onRefreshSuccess) {
          this.onRefreshSuccess(newToken, response.data.user);
        }

        return newToken;
      } catch (error) {
        // 401/403: sessione terminata (durata massima, logout altrove, account disattivato)
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          if (this.onRefreshError) {
            this.onRefreshError(error);
          }
        }
        throw error;
      } finally {
        this.refreshingPromise = null;
      }
    })();

    return this.refreshingPromise;
  }

  // Manually refresh token (e.g. "Continue session")
  manualRefresh() {
    this.setActivityLocked(false);
    this.updateActivity();
    return this.refreshToken();
  }

  // Get debug information about the token service
  getDebugInfo() {
    const info = this.getTokenInfo();

    return {
      refreshAfterMinutes: REFRESH_AFTER / 1000 / 60,
      idleTimeoutMinutes: IDLE_TIMEOUT / 1000 / 60,
      hasActiveTimer: !!this.refreshTimerId,
      isRefreshing: !!this.refreshingPromise,
      lastActivity: new Date(this.getLastActivityTime()).toLocaleString(),
      idleMinutes: Math.floor(this.getIdleTime() / 1000 / 60),
      tokenInfo: info ? {
        issuedAt: new Date(info.issuedAt).toLocaleString(),
        expiresAt: new Date(info.expiresAt).toLocaleString(),
        sessionStart: new Date(info.sessionStart).toLocaleString()
      } : null
    };
  }

  // Clean up when logging out
  cleanup() {
    this.stopTokenRefresh();
    this.activityLocked = false;
    this.lastActivityTime = Date.now();
    this.lastActivityWrite = 0;
    this.onRefreshSuccess = null;
    this.onRefreshError = null;
    try {
      localStorage.removeItem(ACTIVITY_KEY);
    } catch (e) {
      // ignore
    }
  }
}

// Create a singleton instance
const tokenServiceInstance = new TokenService();

// Per debug - SOLO in development
if (process.env.NODE_ENV === 'development') {
  window.tokenService = tokenServiceInstance;
}

// Export the instance
export default tokenServiceInstance;
